import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as datefns from 'date-fns'
import { EnrichedWarrantEntity, Origin, WarrantEntity, WarrantSubscriptionPeriod } from 'types/WarrantEntity'
import WarrantApi from '../../../api/WarrantApi'
import { LeavePageConfirmationDialog } from '../../../components/dialogs/LeavePageConfirmationDialog'
import { DeleteWarrantConfirmationDialog } from './AdminDeleteWarrantConfirmationDialog'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import LinearProgress from '@mui/material/LinearProgress'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { WarrantName } from '../../../components/entities/WarrantName'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import FeeStructureFields from '../shared/AdminFeeStructureFields'
import { WarrantSubPeriods } from './AdminWarrantSubPeriods'
import { WarrantDataForm } from './AdminWarrantDataForm'
import InvestorsFields from '../shared/investors/AdminInvestorsFields'
import Divider from '@mui/material/Divider'
import { FormWarrantEntity } from '../types/FormWarrantEntity'

const warrantApi = new WarrantApi()

const setNullIfEmpty = <T extends Record<string, boolean | number | string | null>, K extends keyof T>(
    obj: T,
    fieldNamesToFix: Array<K>,
) => {
    const mod: { [K in keyof T]: T[K] | null } = { ...obj }

    fieldNamesToFix.forEach((field) => {
        if (obj[field] === '') mod[field] = null
    })

    return mod
}

const castToIntegers = <T, K extends keyof T>(obj: Record<K, unknown>, fieldNamesToFix: Array<K>) => {
    fieldNamesToFix.forEach((field) => {
        const value = obj[field]
        obj[field] = value != null && ('' + value).trim().length > 0 ? parseInt(value as string) : null
    })
    return obj
}

const removeAttributes = <T extends object>(obj: T, fieldNamesToRemove: Array<keyof T>) => {
    fieldNamesToRemove.forEach((field) => delete obj[field])
    return obj
}

const keepAttributes = <T extends object>(obj: T, fieldNamesToKeep: Array<keyof T>) => {
    Object.keys(obj).forEach((field) => {
        if (!fieldNamesToKeep.includes(field as keyof T)) {
            delete obj[field as keyof T]
        }
    })
    return obj
}

const fixDates = (objectWithDates: WarrantSubscriptionPeriod) => {
    const dateFields = ['subStartDate', 'subEndDate', 'vwapPeriodEnd'] as const
    dateFields.forEach((field) => {
        if (field in objectWithDates && objectWithDates[field]) {
            let date = new Date(objectWithDates[field])
            // if the time of day is after 12:00, set the date to the next day
            if (date.getHours() >= 12) {
                date = datefns.addDays(date, 1)
            }
            // replace date with yyyy-mm-dd string
            objectWithDates[field] = datefns.format(date, 'yyyy-MM-dd')
        }
    })
    return objectWithDates
}

const cleanupOrigins = (origin: Origin) => {
    keepAttributes(origin, [
        'id',
        'warrantId',
        'issueId',
        'convertibleId',
        'warrantsPerUnit',
        'numberOfWarrants',
        'externalComment',
    ])
    castToIntegers(origin, ['warrantsPerUnit', 'numberOfWarrants'])
}

const cleanupSubscriptionPeriod = (warrant: WarrantEntity, subPeriod: WarrantSubscriptionPeriod) => {
    subPeriod.warrantId = warrant.id
    removeAttributes(subPeriod, ['createdAt', 'updatedAt', 'prelSubPrice'])
    fixDates(subPeriod)
    if (!subPeriod.isVwap) {
        subPeriod.vwapDiscount = null
        subPeriod.vwapDays = null
        subPeriod.vwapPeriodEnd = null
        subPeriod.priceMinQuota = null
        subPeriod.priceMin = null
        subPeriod.noPriceMax = null
        subPeriod.priceMax = null
    }

    return setNullIfEmpty(subPeriod, [
        'commitments',
        'guarantors',
        'sharesSubscribed',
        'warrantsRequired',
        'sharesReceived',
        'warrantSubPrice',
    ]) as unknown as WarrantSubscriptionPeriod
}

export function EditWarrantForm({
    warrant,
    refetchData,
}: {
    refetchData: () => Promise<void>
    warrant: WarrantEntity
}) {
    const formMethods = useForm<FormWarrantEntity>({
        defaultValues: { ...warrant },
    })
    const [isCustomFormDirty, setIsCustomFormDirty] = useState(false)
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [lastSaved, setLastSaved] = useState<Date | null>(null)
    const [noInvestorsNamedChecked, setNoInvestorsNamedChecked] = React.useState(
        !!formMethods.getValues('noInvestorsNamed'),
    )
    const hasGuarantors = formMethods.getValues('subscriptionPeriods')?.some((sp) => sp.guarantors)
    const hasCommitments = formMethods.getValues('subscriptionPeriods')?.some((sp) => sp.commitments)
    const navigate = useNavigate()

    useEffect(() => {
        formMethods.reset({ ...warrant })
    }, [warrant])

    const handleUpdateWarrant = async () => {
        if (saving) return
        setSaving(true)
        const data = formMethods.getValues() as unknown as EnrichedWarrantEntity
        const removeAttributes: Array<keyof typeof data> = [
            'amountInPercentage',
            'leo',
            'isSeller',
            'closePrice',
            'companyId',
            'companyName',
            'companyCountryCode',
            'createdAt',
            'currency',
            'instrumentIsin',
            'instrumentName',
            'instrumentSymbol',
            'issueId',
            'numberOfWarrants',
            'shareName',
            'startTradeDate',
            'tradesData',
            'tradesStats',
            'totalNumberOfWarrants',
            'updatedAt',
            'warrantsPerUnit',
            'isInWatchList',
            'currentSubscriptionPeriod',
            'totalGuarantorRemunerationSharesSize',
            'totalGuarantorRemunerationShares',
            'totalGuarantorAllocation',
            'totalGuarantorAllocationSize',
            'shareInstrumentId',
        ]
        const removeInvestorAttributes: Array<keyof (typeof data.investors)[number]> = [
            'amountInPercentage',
            'leo',
            'isSeller',
            'withoutRights',
        ]
        removeAttributes.forEach((attr) => {
            data.investors.forEach((investor) => {
                removeInvestorAttributes.forEach((key) => {
                    delete investor[key]
                })
            })
            delete data[attr]
        })
        if (data.origins) {
            data.origins.forEach(cleanupOrigins)
        }

        if (data.subscriptionPeriods) {
            data.subscriptionPeriods = data.subscriptionPeriods.map((period) =>
                cleanupSubscriptionPeriod(warrant, period),
            )
        }

        const updatedWarrant = await warrantApi.update(warrant.id, data)
        if (updatedWarrant) {
            setLastSaved(new Date())
            void refetchData()
        } else {
            console.log('error result: no updated warrant')
        }
        setIsCustomFormDirty(false)
        setSaving(false)
    }
    const handleDeleteWarrant = async () => {
        if (saving) return
        setSaving(true)
        const allDeleted = await warrantApi.delete(warrant.id)
        console.log({ allDeleted })
        navigate('/admin/warrants')
        setSaving(false)
    }

    const warrantSubPeriods = formMethods.watch('subscriptionPeriods')

    const warrantSubPeriodNotValid = warrantSubPeriods.some((period) => {
        return period.warrantSubPrice !== null && parseFloat(period.warrantSubPrice) === 0
    })

    const disableSave = warrantSubPeriodNotValid

    return (
        <FormProvider {...formMethods}>
            <LeavePageConfirmationDialog isDirty={formMethods.formState.isDirty || isCustomFormDirty} />
            <DeleteWarrantConfirmationDialog
                open={deleteConfirmationOpen}
                onClose={() => setDeleteConfirmationOpen(false)}
                onDelete={handleDeleteWarrant}
            />
            <Card sx={{ mb: 6 }}>
                <CardContent>
                    {!warrant && <LinearProgress />}
                    {warrant && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography
                                    variant='h5'
                                    gutterBottom
                                    sx={{
                                        display: 'block',
                                    }}
                                >
                                    <WarrantName warrant={warrant} />
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant='body1'>Aktie: {warrant.shareName}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 1 }}>
                                <Button
                                    variant='outlined'
                                    color='warning'
                                    fullWidth
                                    onClick={() => setDeleteConfirmationOpen(true)}
                                >
                                    Radera
                                </Button>
                            </Grid>
                            <Grid size={{ xs: 12, md: 1 }}>
                                <Stack sx={{ textAlign: 'center' }}>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        fullWidth
                                        onClick={formMethods.handleSubmit(handleUpdateWarrant)}
                                        disabled={disableSave}
                                    >
                                        Spara {saving && <CircularProgress size={24} />}
                                    </Button>
                                    <Typography variant='caption' color='textSecondary'>
                                        {lastSaved && datefns.format(lastSaved, 'H:mm:ss')}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    )}
                    <Divider sx={{ my: 4 }} />
                    {warrant && <WarrantDataForm warrant={warrant} setIsCustomFormDirty={setIsCustomFormDirty} />}
                </CardContent>
            </Card>
            {hasGuarantors && (
                <Card sx={{ padding: 4, mb: 6 }}>
                    <Typography
                        variant='h5'
                        gutterBottom
                        sx={{
                            display: 'block',
                        }}
                    >
                        Garantiarvoden
                    </Typography>
                    <FeeStructureFields setIsCustomFormDirty={setIsCustomFormDirty} />
                </Card>
            )}
            {(hasGuarantors || hasCommitments) && (
                <Card sx={{ padding: 4, mb: 6 }}>
                    <Typography
                        variant='h5'
                        gutterBottom
                        sx={{
                            display: 'block',
                        }}
                    >
                        Investerare
                    </Typography>
                    <InvestorsFields
                        entityType='warrant'
                        acceptedFeeLevels={
                            formMethods
                                .watch('fees')
                                ?.filter((fee) => fee.feeLevel !== null)
                                .map(({ feeLevel }) => +(feeLevel as unknown as string)) || []
                        }
                        currency={warrant.currency}
                        isFeeLevelEnabled
                        isLeoEnabled={false}
                        isPercentageEnabled={false}
                        isSellerEnabled={false}
                        isPutOptionEnabled
                        sanityChecker={() => ({
                            warnings: {
                                warningOfferedUnits: false,
                                totalOfferingCash: null,
                            },
                            totals: {
                                cash: 0,
                                percentage: 0,
                                level0: 0,
                                level1: 0,
                            },
                        })}
                        setIsCustomFormDirty={setIsCustomFormDirty}
                        noInvestorsNamedChecked={noInvestorsNamedChecked}
                        setNoInvestorsNamedChecked={setNoInvestorsNamedChecked}
                        validateAmount={() => true}
                        validateLeo={() => true}
                    />
                </Card>
            )}
            {warrant && (
                <Card sx={{ padding: 4, mb: 6 }}>
                    <CardContent>
                        <WarrantSubPeriods warrant={warrant} setIsCustomFormDirty={setIsCustomFormDirty} />
                    </CardContent>
                </Card>
            )}
        </FormProvider>
    )
}
